import React, { useState } from 'react';
import { CATEGORIES } from '../constants/categories';
import { FaTimes, FaChevronDown } from 'react-icons/fa';

const JobFilters = ({ filters, onFilterChange, onClearFilters }) => {
  // ── STATE (UNCHANGED) ──────────────────────────────────────────────
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salaryRange, setSalaryRange] = useState({
    min: filters.salaryMin || '',
    max: filters.salaryMax || '',
  });
  const [experienceRange, setExperienceRange] = useState({
    min: filters.experienceMin || '',
    max: filters.experienceMax || '',
  });

  // ── NEW: Accordion open/close (purely UI state, no filter logic) ──
  const [openSections, setOpenSections] = useState({
    category:       true,
    workMode:       true,
    employmentType: false,
    salary:         false,
    experience:     false,
    posted:         false,
  });

  // ── DATA ARRAYS (UNCHANGED) ────────────────────────────────────────
  const workModes      = ['On-site', 'Remote', 'Hybrid'];
  const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'];
  const postedWithin   = ['24 hours', '3 days', '7 days', '14 days', '30 days'];

  // ── HANDLERS (UNCHANGED) ──────────────────────────────────────────
  const handleSalaryChange = (type, value) => {
    const newSalary = { ...salaryRange, [type]: value };
    setSalaryRange(newSalary);
    if (type === 'min') onFilterChange('salaryMin', value);
    if (type === 'max') onFilterChange('salaryMax', value);
  };

  const handleExperienceChange = (type, value) => {
    const newExp = { ...experienceRange, [type]: value };
    setExperienceRange(newExp);
    if (type === 'min') onFilterChange('experienceMin', value);
    if (type === 'max') onFilterChange('experienceMax', value);
  };

  // ── UI HELPERS (new, read-only) ────────────────────────────────────
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Count active filters for badge display
  const activeCount = [
    filters.category,
    filters.workMode,
    filters.employmentType,
    filters.salaryMin,
    filters.salaryMax,
    filters.experienceMin,
    filters.experienceMax,
    filters.postedWithin,
  ].filter(Boolean).length;

  // ── ACCORDION SECTION WRAPPER ──────────────────────────────────────
  const FilterSection = ({ title, sectionKey, children }) => (
    <div className={`filter-section ${openSections[sectionKey] ? 'is-open' : ''}`}>
      <button
        className="filter-section-header"
        onClick={() => toggleSection(sectionKey)}
      >
        <span className="filter-section-title">{title}</span>
        <FaChevronDown
          className={`filter-chevron${openSections[sectionKey] ? ' open' : ''}`}
          size={12}
        />
      </button>
      <div className={`filter-section-body ${openSections[sectionKey] ? 'open' : ''}`}>
        <div className="filter-section-content">
          {children}
        </div>
      </div>
    </div>
  );

  // ── MAIN FILTER CONTENT ────────────────────────────────────────────
  const FilterContent = () => (
    <div className="filter-content-wrapper">

      {/* ── Category (radio — UNCHANGED logic) ── */}
      <FilterSection title="Category" sectionKey="category">
        <div className="filter-options-list">
          {CATEGORIES.map((category) => (
            <label
              key={category}
              className={`filter-radio-label${filters.category === category ? ' checked' : ''}`}
            >
              <input
                type="radio"
                name="category"
                checked={filters.category === category}
                onChange={() => onFilterChange('category', category)}
                className="filter-radio-input"
              />
              <span className="filter-radio-custom" />
              <span className="filter-radio-text">{category}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* ── Work Mode (radio — UNCHANGED logic, styled as pills) ── */}
      <FilterSection title="Work Mode" sectionKey="workMode">
        <div className="filter-pills-group">
          {workModes.map((mode) => (
            <label
              key={mode}
              className={`filter-mode-pill${filters.workMode === mode ? ' active' : ''}`}
            >
              <input
                type="radio"
                name="workMode"
                checked={filters.workMode === mode}
                onChange={() => onFilterChange('workMode', mode)}
                style={{ display: 'none' }}
              />
              {mode}
            </label>
          ))}
        </div>
      </FilterSection>

      {/* ── Employment Type (radio — UNCHANGED logic) ── */}
      <FilterSection title="Employment Type" sectionKey="employmentType">
        <div className="filter-options-list">
          {employmentTypes.map((type) => (
            <label
              key={type}
              className={`filter-radio-label${filters.employmentType === type ? ' checked' : ''}`}
            >
              <input
                type="radio"
                name="employmentType"
                checked={filters.employmentType === type}
                onChange={() => onFilterChange('employmentType', type)}
                className="filter-radio-input"
              />
              <span className="filter-radio-custom" />
              <span className="filter-radio-text">{type}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* ── Salary Range (UNCHANGED logic) ── */}
      <FilterSection title="Salary Range (LPA)" sectionKey="salary">
        <div className="filter-range-inputs">
          <div className="range-input-group">
            <label>Min (LPA)</label>
            <input
              type="number"
              value={salaryRange.min}
              onChange={(e) => handleSalaryChange('min', e.target.value)}
              placeholder="e.g., 5"
              className="filter-number-input"
            />
          </div>
          <span className="range-separator">–</span>
          <div className="range-input-group">
            <label>Max (LPA)</label>
            <input
              type="number"
              value={salaryRange.max}
              onChange={(e) => handleSalaryChange('max', e.target.value)}
              placeholder="e.g., 20"
              className="filter-number-input"
            />
          </div>
        </div>
      </FilterSection>

      {/* ── Experience Range (UNCHANGED logic) ── */}
      <FilterSection title="Experience (Years)" sectionKey="experience">
        <div className="filter-range-inputs">
          <div className="range-input-group">
            <label>Min (Yrs)</label>
            <input
              type="number"
              value={experienceRange.min}
              onChange={(e) => handleExperienceChange('min', e.target.value)}
              placeholder="0"
              className="filter-number-input"
            />
          </div>
          <span className="range-separator">–</span>
          <div className="range-input-group">
            <label>Max (Yrs)</label>
            <input
              type="number"
              value={experienceRange.max}
              onChange={(e) => handleExperienceChange('max', e.target.value)}
              placeholder="10"
              className="filter-number-input"
            />
          </div>
        </div>
      </FilterSection>

      {/* ── Posted Within (radio — UNCHANGED logic, styled as pills) ── */}
      <FilterSection title="Posted Within" sectionKey="posted">
        <div className="filter-pills-group">
          {postedWithin.map((time) => (
            <label
              key={time}
              className={`filter-mode-pill${filters.postedWithin === time ? ' active' : ''}`}
            >
              <input
                type="radio"
                name="postedWithin"
                checked={filters.postedWithin === time}
                onChange={() => onFilterChange('postedWithin', time)}
                style={{ display: 'none' }}
              />
              {time}
            </label>
          ))}
        </div>
      </FilterSection>
    </div>
  );

  // ── RENDER ────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Mobile FAB Filter Button ── */}
      <button
        onClick={() => setShowMobileFilters(true)}
        className="mobile-filter-fab"
        aria-label="Open filters"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 4h18v2.586l-7 7V20l-4 2v-8.414l-7-7V4z" />
        </svg>
        {activeCount > 0 && <span className="fab-badge">{activeCount}</span>}
      </button>

      {/* ── Desktop Filter Sidebar Card ── */}
      <div className="filter-sidebar-card">
        {/* Teal gradient header */}
        <div className="filter-sidebar-header">
          <div className="filter-header-left">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 4h18v2.586l-7 7V20l-4 2v-8.414l-7-7V4z" />
            </svg>
            Filter Jobs
          </div>

          {activeCount > 0 && (
            <div className="filter-active-badge-row">
              <span className="filter-active-count">{activeCount} active</span>
              <button
                onClick={() => {
                  onClearFilters();
                  setSalaryRange({ min: '', max: '' });
                  setExperienceRange({ min: '', max: '' });
                }}
                className="filter-clear-all"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Filter accordion body */}
        <FilterContent />
      </div>

      {/* ── Mobile Drawer (UNCHANGED close/clear logic) ── */}
      {showMobileFilters && (
        <div className="mobile-filter-overlay">
          <div
            className="mobile-filter-backdrop"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="mobile-filter-drawer">
            <div className="mobile-filter-header">
              <h3>
                Filter Jobs
                {activeCount > 0 && ` (${activeCount})`}
              </h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="mobile-filter-close"
                aria-label="Close filters"
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="mobile-filter-body">
              <FilterContent />
            </div>

            <div className="mobile-filter-footer">
              <button
                onClick={() => {
                  onClearFilters();
                  setSalaryRange({ min: '', max: '' });
                  setExperienceRange({ min: '', max: '' });
                  setShowMobileFilters(false);
                }}
                className="mobile-filter-clear"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="mobile-filter-apply"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JobFilters;
